import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Button,
  FormHelperText,
} from "@mui/material";
import apiClient from "../../../utils/apiClient";

const UndergraduateStep = ({ data, setData, handleNext, handleBack }) => {
  const [loading, setLoading] = useState(true);
  const gradingOptions = ["Percentage", "CGPA", "Grades"];
  const courseOptions = [
    "Mathematics",
    "Computer Science",
    "Physics",
    "Chemistry",
    "Others",
  ];

  const marksOptions = {
    Percentage: ["Not applicable", "30-40%", "40-50%", "50-60%", "Above 60%"],
    CGPA: ["Not applicable", "5-6", "6-7", "7-8", "Above 8"],
    Grades: ["Not applicable", "C", "B", "A", "A+"],
  };

  const [errors, setErrors] = useState({});

  const uiFieldStyles = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#ffffff",
      borderRadius: "6px",
      "& fieldset": { borderColor: "#c9c9c9" },
      "&:hover fieldset": { borderColor: "#9d9d9d" },
      "&.Mui-focused fieldset": { borderColor: "#000" },
    },
    "& .MuiInputLabel-root": { color: "#444" },
  };

  const updateField = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleCourseChange = (course) => {
    let updated = [...(data.courses || [])];

    if (updated.includes(course)) {
      updated = updated.filter((c) => c !== course);
    } else {
      updated.push(course);
    }

    updateField("courses", updated);
    updateField(
      "marks",
      updated.reduce((acc, c) => ({ ...acc, [c]: "" }), {}),
    );

    setErrors((prev) => ({ ...prev, courses: "" }));
  };

  const handleMarksChange = (course, value) => {
    setData((prev) => ({
      ...prev,
      marks: { ...prev.marks, [course]: value },
    }));

    setErrors((prev) => ({
      ...prev,
      marks: { ...(prev.marks || {}), [course]: "" },
    }));
  };

  const isOtherSelected = data.courses?.includes("Others");

  useEffect(() => {
    const fetchUgData = async () => {
      try {
        const res = await apiClient.get("/counselling/ug-info");

        if (res.data?.ug_data) {
          const ugData = res.data.ug_data;

          setData((prev) => ({
            ...prev,
            ...ugData,
            courses: ugData.courses || [],
            marks: ugData.marks || {},
          }));
        }
      } catch (error) {
        console.error("Failed to fetch UG info:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUgData();
  }, []);

  // ---------------- VALIDATION FUNCTION ----------------
  const validateForm = () => {
    let newErrors = {};

    if (!data.university) newErrors.university = "University name is required";
    if (!data.location) newErrors.location = "Location is required";
    if (!data.years) newErrors.years = "Years attended are required";
    if (!data.degree) newErrors.degree = "Degree is required";
    if (!data.branch) newErrors.branch = "Branch is required";
    if (!data.grading) newErrors.grading = "Select a grading system";

    if (!data.courses || data.courses.length === 0) {
      newErrors.courses = "Select at least one course";
    }

    if (isOtherSelected && !data.otherCourse) {
      newErrors.otherCourse = "Please specify the course";
    }

    // Marks validation
    const marksErrors = {};
    if (data.courses) {
      data.courses.forEach((c) => {
        if (!data.marks?.[c]) {
          marksErrors[c] = "Select marks";
        }
      });
    }

    if (Object.keys(marksErrors).length > 0) newErrors.marks = marksErrors;

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const handleNextClick = async () => {
    if (validateForm()) {
      await apiClient.post("/counselling/ug-info", { ugData: data });
      handleNext();
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: "#d8f1f6",
        padding: { xs: 3, sm: 5 },
        borderRadius: "40px 40px 0 0",
        minHeight: "100vh",
      }}
    >
      {/* Title */}
      <Typography
        sx={{
          fontSize: { xs: "22px", sm: "24px", md: "28px" },
          fontWeight: 600,
          mb: 1,
        }}
      >
        Undergraduate Education
      </Typography>

      <Typography sx={{ fontSize: "18px", fontWeight: 600, mb: 4 }}>
        Provide your Undergraduate Academic Details
      </Typography>

      <Grid container spacing={3}>
        {/* University */}
        <Grid item xs={12} md={6}>
          <Typography sx={{ mb: 1, fontWeight: 600 }}>
            University/College Name*
          </Typography>
          <TextField
            fullWidth
            placeholder="Enter your university name"
            sx={uiFieldStyles}
            value={data.university || ""}
            onChange={(e) => updateField("university", e.target.value)}
            error={Boolean(errors.university)}
            helperText={errors.university}
          />
        </Grid>

        {/* Location */}
        <Grid item xs={12} md={6}>
          <Typography sx={{ mb: 1, fontWeight: 600 }}>
            Location / City*
          </Typography>
          <FormControl
            fullWidth
            sx={uiFieldStyles}
            error={Boolean(errors.location)}
          >
            <Select
              displayEmpty
              value={data.location || ""}
              onChange={(e) => updateField("location", e.target.value)}
            >
              <MenuItem value="" disabled>
                Select City
              </MenuItem>
              <MenuItem value="Delhi">Delhi</MenuItem>
              <MenuItem value="Mumbai">Mumbai</MenuItem>
              <MenuItem value="Bengaluru">Bengaluru</MenuItem>
              <MenuItem value="Chennai">Chennai</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
            <FormHelperText>{errors.location}</FormHelperText>
          </FormControl>
        </Grid>

        {/* Years attended */}
        <Grid item xs={12} md={6}>
          <Typography sx={{ mb: 1, fontWeight: 600 }}>
            Years Attended*
          </Typography>
          <TextField
            fullWidth
            placeholder="2020 - 2024"
            sx={uiFieldStyles}
            value={data.years || ""}
            onChange={(e) => updateField("years", e.target.value)}
            error={Boolean(errors.years)}
            helperText={errors.years}
          />
        </Grid>

        {/* Degree */}
        <Grid item xs={12} md={6}>
          <Typography sx={{ mb: 1, fontWeight: 600 }}>Degree*</Typography>
          <TextField
            fullWidth
            placeholder="B.Tech, B.Sc, BCA..."
            sx={uiFieldStyles}
            value={data.degree || ""}
            onChange={(e) => updateField("degree", e.target.value)}
            error={Boolean(errors.degree)}
            helperText={errors.degree}
          />
        </Grid>

        {/* Branch */}
        <Grid item xs={12} md={6}>
          <Typography sx={{ mb: 1, fontWeight: 600 }}>
            Branch / Specialization*
          </Typography>
          <TextField
            fullWidth
            placeholder="Computer Science, IT, ECE..."
            sx={uiFieldStyles}
            value={data.branch || ""}
            onChange={(e) => updateField("branch", e.target.value)}
            error={Boolean(errors.branch)}
            helperText={errors.branch}
          />
        </Grid>

        {/* Grading */}
        <Grid item xs={12} md={6}>
          <Typography sx={{ mb: 1, fontWeight: 600 }}>
            Select Grading System*
          </Typography>
          <FormControl
            fullWidth
            sx={uiFieldStyles}
            error={Boolean(errors.grading)}
          >
            <Select
              displayEmpty
              value={data.grading || ""}
              onChange={(e) => updateField("grading", e.target.value)}
            >
              <MenuItem value="" disabled>
                Select Grading System
              </MenuItem>
              {gradingOptions.map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{errors.grading}</FormHelperText>
          </FormControl>
        </Grid>

        {/* Courses */}
        <Grid item xs={12}>
          <Typography sx={{ mb: 2, fontWeight: 600 }}>
            Select Courses*
          </Typography>
          <Box
            sx={{
              background: "white",
              borderRadius: "10px",
              padding: 2,
            }}
          >
            {courseOptions.map((course) => (
              <FormControlLabel
                key={course}
                control={
                  <Checkbox
                    checked={data.courses?.includes(course) || false}
                    onChange={() => handleCourseChange(course)}
                  />
                }
                label={course}
              />
            ))}
          </Box>
          {errors.courses && (
            <Typography color="error" sx={{ mt: 1, fontSize: "14px" }}>
              {errors.courses}
            </Typography>
          )}
        </Grid>

        {/* Other Course */}
        {isOtherSelected && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              placeholder="Enter other course name"
              sx={uiFieldStyles}
              value={data.otherCourse || ""}
              onChange={(e) => updateField("otherCourse", e.target.value)}
              error={Boolean(errors.otherCourse)}
              helperText={errors.otherCourse}
            />
          </Grid>
        )}

        {/* Marks */}
        {data.grading &&
          data.courses?.map((course) => (
            <Grid item xs={12} key={course}>
              <Typography sx={{ mb: 1, fontWeight: 600 }}>
                Marks for{" "}
                {course === "Others" ? data.otherCourse || "Other" : course}
              </Typography>

              <RadioGroup
                value={data.marks?.[course] || ""}
                onChange={(e) => handleMarksChange(course, e.target.value)}
              >
                {marksOptions[data.grading].map((m) => (
                  <FormControlLabel
                    key={m}
                    value={m}
                    control={<Radio />}
                    label={m}
                  />
                ))}
              </RadioGroup>

              {errors.marks?.[course] && (
                <Typography color="error" sx={{ mt: 1, fontSize: "14px" }}>
                  {errors.marks[course]}
                </Typography>
              )}
            </Grid>
          ))}

        {/* Buttons */}
        <Grid item xs={12} mt={5} display="flex" justifyContent="space-between">
          <Button
            variant="outlined"
            onClick={handleBack}
            sx={{
              borderColor: "#2C2C2C",
              color: "#2C2C2C",
              padding: "6px 28px",
            }}
          >
            Back
          </Button>

          <Button
            variant="contained"
            onClick={handleNextClick}
            sx={{
              backgroundColor: "#2C2C2C",
              color: "white",
              padding: "6px 28px",
              borderRadius: "6px",
              "&:hover": { backgroundColor: "#444" },
            }}
          >
            Next
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UndergraduateStep;
